import React, { useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDocuments } from '../contexts/DocumentContext';
import BatchRenameModal from './BatchRenameModal';
import { devLog } from '../utils/devLog';

export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  progress: number;
  status: 'uploading' | 'processing' | 'ocr_processing' | 'completed' | 'error';
  errorMessage?: string;
  uploadedAt: Date;
  file?: File;
  nameChanged?: boolean;
  originalRequestedName?: string;
  finalName?: string;
  duplicateOfDocumentId?: string;
  documentType?: string; // Document type (Quote, Binder, Policy, Loss Run, Other)
  processingMetadata?: {
    extractionMethod?: 'pdf_only' | 'ocr_only' | 'hybrid' | 'pdf_fallback';
    extractionQuality?: 'high' | 'medium' | 'low' | 'failed';
    confidence?: number;
    ocrAttempted?: boolean;
    processingTime?: number;
  };
}

export interface UploadCompleteInfo {
  success: boolean;
  documentId: string;
  originalName?: string;
  finalName?: string;
  nameChanged?: boolean;
  message?: string;
}

interface FileUploaderProps {
  onFilesUploaded: (files: UploadedFile[]) => void;
  onUploadComplete?: (info: UploadCompleteInfo) => void;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  acceptedTypes?: string[];
  className?: string;
  disabled?: boolean;
  customerId?: string;
  projectId?: string;
  hideIndividualSuccessMessages?: boolean; // Hide "Ready for analysis" messages in workflow contexts
}

const FileUploader: React.FC<FileUploaderProps> = ({
  onFilesUploaded,
  onUploadComplete,
  maxFiles = 10,
  maxFileSize = 50,
  acceptedTypes = [
    'application/pdf',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'text/csv',
    'application/csv',
    'message/rfc822', // .eml
    'application/vnd.ms-outlook' // .msg
  ],
  className = '',
  disabled = false,
  customerId,
  projectId,
  hideIndividualSuccessMessages = false
}) => {
  const { token } = useAuth();
  const { currentProjectId } = useDocuments();
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<UploadedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Batch rename dialog state — one table, one row per file, instead of a
  // modal-per-file Next/Skip flow.
  const [renameDialog, setRenameDialog] = useState<{ isOpen: boolean; files: File[] }>({
    isOpen: false,
    files: [],
  });

  const generateFileId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Read first part of file for validation
  const readFileStart = (file: File, bytes: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(file.slice(0, bytes));
    });
  };

  // Basic email file validation
  const isValidEmailFileStart = (content: string): boolean => {
    const emailPatterns = [
      /^From:/im,
      /^Subject:/im,
      /^Date:/im,
      /^Message-ID:/im,
      /^MIME-Version:/im,
      /^To:/im
    ];
    
    return emailPatterns.some(pattern => pattern.test(content));
  };

  // Timeout configuration for different file types
  const OCR_UPLOAD_TIMEOUT = 1200000; // 1200 seconds (20 minutes) for OCR-requiring files
  const EMAIL_UPLOAD_TIMEOUT = 120000; // 120 seconds (2 minutes) for email uploads
  const REGULAR_UPLOAD_TIMEOUT = 30000; // 30 seconds for regular files

  const uploadFileToBackend = async (fileData: UploadedFile): Promise<UploadedFile> => {
    devLog('uploadFileToBackend called for:', fileData.name, 'Type:', fileData.type);
    
    try {
      // Check if it's an email file
      const isEmailFile = fileData.type === 'message/rfc822' || 
                         fileData.type === 'application/vnd.ms-outlook' ||
                         fileData.name.toLowerCase().endsWith('.eml') ||
                         fileData.name.toLowerCase().endsWith('.msg');
      
      devLog('Email file check:', { fileName: fileData.name, isEmailFile, type: fileData.type });
      
      // Update status for email files
      if (isEmailFile) {
        setUploadingFiles(prev => 
          prev.map(f => 
            f.id === fileData.id 
              ? { ...f, isEmail: true }
              : f
          )
        );
      }
      
      // Validate file before upload
      if (isEmailFile && fileData.file) {
        // Quick validation of email file structure
        try {
          const fileStart = await readFileStart(fileData.file, 1000);
          if (!isValidEmailFileStart(fileStart)) {
            throw new Error('File does not appear to be a valid email format');
          }
        } catch (error) {
          console.warn('Email validation warning:', error);
          // Continue with upload even if validation fails
        }
        
        // Show warning for large files
        if (fileData.file.size > 5 * 1024 * 1024) { // 5MB
          console.warn(`Large email file detected: ${(fileData.file.size / 1024 / 1024).toFixed(1)}MB. Upload may take longer.`);
        }
      }

      // Use the main documents endpoint for all files (including emails which will be converted to PDF)
      const uploadEndpoint = `${process.env.REACT_APP_API_BASE_URL || '/api'}/documents`;

      // Create form data
      const formData = new FormData();
      if (fileData.file) {
        formData.append('file', fileData.file);
        if (customerId) {
          formData.append('customerId', customerId);
        }
        // Use projectId prop if provided, otherwise use currentProjectId from context
        const effectiveProjectId = projectId || currentProjectId;
        if (effectiveProjectId) {
          formData.append('projectId', effectiveProjectId);
        }
        if (fileData.documentType) {
          formData.append('policyType', fileData.documentType);
        }
      }

      // Get auth token from useAuth hook
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      devLog('Using token from AuthContext:', token.substring(0, 20) + '...');

      // Determine appropriate timeout based on file type
      const mightRequireOCR = fileData.type === 'application/pdf' || 
                             fileData.type.startsWith('image/') ||
                             fileData.name.toLowerCase().endsWith('.pdf');
      
      let uploadTimeout = REGULAR_UPLOAD_TIMEOUT;
      if (isEmailFile) {
        uploadTimeout = EMAIL_UPLOAD_TIMEOUT;
      } else if (mightRequireOCR) {
        uploadTimeout = OCR_UPLOAD_TIMEOUT;
      }
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, uploadTimeout);

      try {
        devLog(`Starting upload with ${uploadTimeout/1000}s timeout for:`, fileData.name);
        devLog('Upload endpoint:', uploadEndpoint);
        
        // Upload file with timeout
        const response = await fetch(uploadEndpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        devLog(`Upload response received for ${fileData.name}:`, response.status, response.statusText);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Upload failed', code: 'UNKNOWN_ERROR' }));
          
          // Handle specific backend error codes
          let userFriendlyMessage = errorData.error || `Upload failed with status ${response.status}`;
          
          if (errorData.code === 'MEMORY_ERROR') {
            userFriendlyMessage = 'Server is experiencing high memory usage. Please try uploading a smaller file or wait a moment and try again.';
          } else if (errorData.code === 'TIMEOUT_ERROR') {
            userFriendlyMessage = 'Document processing timed out. Please try a smaller file or simpler document.';
          } else if (errorData.code === 'FILE_TOO_LARGE') {
            userFriendlyMessage = errorData.error;
          } else if (errorData.type === 'email_processing_error') {
            userFriendlyMessage = errorData.details || errorData.error;
          }
          
          console.error(`Upload failed for ${fileData.name}:`, {
            status: response.status,
            error: errorData.error,
            code: errorData.code,
            type: errorData.type,
            timestamp: errorData.timestamp
          });
          
          throw new Error(userFriendlyMessage);
        }
        
        const data = await response.json();
      
      // For all uploads, return the document ID
      const documentId = data.id;
      
      devLog('Upload response:', { 
        fileName: fileData.name, 
        isEmailFile, 
        responseData: data, 
        extractedId: documentId 
      });
      
      if (!documentId) {
        console.error('No document ID received from backend!', { 
          fileName: fileData.name, 
          response: data 
        });
        throw new Error('No document ID received from backend');
      }
      
        // Check if the document name was automatically changed
        if (data.nameChanged) {
          devLog(`Document name auto-renamed: "${data.originalRequestedName}" -> "${data.finalName}"`);
          
          // Show notification to user about name change
          if (onUploadComplete) {
            // Pass name change info to parent component
            onUploadComplete({
              success: true,
              documentId: documentId,
              originalName: data.originalRequestedName,
              finalName: data.finalName,
              nameChanged: true,
              message: `Document uploaded successfully. Name was changed to "${data.finalName}" to avoid conflicts.`
            });
          }
        }

        // Return updated file data with backend ID and name info
        return {
          ...fileData,
          id: documentId,
          name: data.finalName || fileData.name, // Use final name from backend
          progress: 100,
          status: 'completed',
          nameChanged: data.nameChanged || false,
          originalRequestedName: data.originalRequestedName,
          finalName: data.finalName,
          duplicateOfDocumentId: data.duplicateOfDocumentId || undefined
        };
      } catch (error) {
        clearTimeout(timeoutId);
        
        if (error instanceof Error && error.name === 'AbortError') {
          let timeoutMessage = 'File upload timed out. Please check your connection and try again.';
          if (isEmailFile) {
            timeoutMessage = 'Email processing timed out. The email may be too large or complex. Please try again with a smaller email file.';
          } else if (mightRequireOCR) {
            timeoutMessage = 'OCR processing timed out. The file may be too large or complex for text extraction. Please try again or use a smaller file.';
          }
          throw new Error(timeoutMessage);
        }
        
        throw error;
      }
    } catch (error) {
      console.error(`Upload failed for ${fileData.name}:`, error);
      
      if (error instanceof Error) {
        console.error(`=
 Error details:`, {
          name: error.name,
          message: error.message,
          stack: error.stack?.split('\n').slice(0, 3).join('\n')
        });
        
        if (error.name === 'AbortError') {
          const isEmail = fileData.type === 'message/rfc822' || 
                         fileData.type === 'application/vnd.ms-outlook' ||
                         fileData.name.toLowerCase().endsWith('.eml') ||
                         fileData.name.toLowerCase().endsWith('.msg');
          
          const requiresOCR = fileData.type === 'application/pdf' || 
                             fileData.type.startsWith('image/') ||
                             fileData.name.toLowerCase().endsWith('.pdf');
          
          let timeoutSeconds = REGULAR_UPLOAD_TIMEOUT / 1000;
          if (isEmail) {
            timeoutSeconds = EMAIL_UPLOAD_TIMEOUT / 1000;
          } else if (requiresOCR) {
            timeoutSeconds = OCR_UPLOAD_TIMEOUT / 1000;
          }
          
          console.error(`Upload timeout after ${timeoutSeconds}s for ${fileData.name}`);
        }
      }
      
      // Extract more detailed error information
      let detailedError = 'Upload failed';
      
      if (error instanceof Error) {
        detailedError = error.message;
        
        // Email-specific error handling
        if (detailedError.includes('Invalid email format')) {
          detailedError = 'Invalid email file format. Please ensure the file is a valid .eml or .msg file.';
        } else if (detailedError.includes('Email file parsing failed')) {
          detailedError = 'Failed to parse email content. The file may be corrupted.';
        } else if (detailedError.includes('Email file is too large')) {
          detailedError = `Email file exceeds the ${maxFileSize}MB size limit.`;
        } else if (detailedError.includes('This email has already been processed')) {
          detailedError = 'This email has already been uploaded and processed.';
        } else if (detailedError.includes('timeout')) {
          detailedError = 'Email processing timed out. The file may be too complex or large.';
        }
      }
      
      // Update file status with detailed error
      setUploadingFiles(prev => 
        prev.map(f => 
          f.id === fileData.id 
            ? { ...f, status: 'error', errorMessage: detailedError }
            : f
        )
      );
      throw error;
    }
  };

  const handleFiles = useCallback(async (files: FileList) => {
    if (files.length === 0) return;

    const fileArray = Array.from(files);

    // Validate file count
    if (uploadingFiles.length + fileArray.length > maxFiles) {
      setInlineError(`Cannot upload more than ${maxFiles} files at once`);
      window.setTimeout(() => setInlineError(null), 5000);
      return;
    }

    setInlineError(null);
    // Open the batch rename table for all files
    setRenameDialog({ isOpen: true, files: fileArray });
    return; // Exit early - processing will happen after rename confirm
  }, [maxFiles, uploadingFiles.length]);

  // Original file processing logic moved to separate function
  const processRenamedFiles = useCallback(async (renamedFiles: Array<{file: File; newName: string; documentType: string}>) => {
    const validateFile = (file: File): string | null => {
      // Check file type and extension for emails
      const isEmailFile = file.type === 'message/rfc822' || 
                         file.type === 'application/vnd.ms-outlook' ||
                         file.name.toLowerCase().endsWith('.eml') ||
                         file.name.toLowerCase().endsWith('.msg');
      
      // Check file type and extension for Excel/CSV
      const isSpreadsheetFile = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                               file.type === 'application/vnd.ms-excel' ||
                               file.type === 'text/csv' ||
                               file.type === 'application/csv' ||
                               file.name.toLowerCase().endsWith('.xlsx') ||
                               file.name.toLowerCase().endsWith('.xls') ||
                               file.name.toLowerCase().endsWith('.csv');

      // Check file type and extension for Word docs (.doc, .docx)
      const isWordFile = file.type === 'application/msword' ||
                         file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                         file.name.toLowerCase().endsWith('.docx') ||
                         file.name.toLowerCase().endsWith('.doc');

      // Check file type and extension for PDF
      const isPdfFile = file.type === 'application/pdf' ||
                        file.name.toLowerCase().endsWith('.pdf');

      // acceptedTypes may be MIME types ('application/pdf') or extensions ('.pdf') depending on caller.
      // Match against either form so workflow contexts that pass extensions still validate correctly.
      const lowerName = file.name.toLowerCase();
      const acceptedByList = acceptedTypes.some(t =>
        t === file.type || (t.startsWith('.') && lowerName.endsWith(t.toLowerCase()))
      );

      if (!acceptedByList && !isEmailFile && !isSpreadsheetFile && !isWordFile && !isPdfFile) {
        return `File type not supported. Please upload PDF, DOC, DOCX, Excel, CSV, or email files (.eml, .msg) only.`;
      }

      // Check file size (convert MB to bytes)
      if (file.size > maxFileSize * 1024 * 1024) {
        return `File size exceeds ${maxFileSize}MB limit.`;
      }

      return null;
    };

    const newFiles: UploadedFile[] = [];
    
    for (const { file, newName, documentType } of renamedFiles) {
      const validationError = validateFile(file);
      // Create a new File object with the renamed name
      const renamedFile = new File([file], newName, { type: file.type });
      
      const uploadedFile: UploadedFile = {
        id: generateFileId(),
        name: newName, // Use the renamed name
        size: file.size,
        type: file.type,
        progress: 0,
        status: validationError ? 'error' : 'uploading',
        errorMessage: validationError || undefined,
        uploadedAt: new Date(),
        file: renamedFile, // Use the renamed file
        documentType: documentType
      };
      
      newFiles.push(uploadedFile);
    }
    
    setUploadingFiles(prev => [...prev, ...newFiles]);
    
    // Process valid files
    const validFiles = newFiles.filter(f => f.status !== 'error');
    
    // Update progress during upload
    const uploadPromises = validFiles.map(async (fileData) => {
      try {
        devLog('Starting upload for file:', fileData.name, 'ID:', fileData.id);
        
        // Update status to uploading
        setUploadingFiles(prev => 
          prev.map(f => 
            f.id === fileData.id 
              ? { ...f, progress: 50, status: 'uploading' }
              : f
          )
        );

        // Upload to backend
        devLog('Calling uploadFileToBackend for:', fileData.name);
        const uploadedFile = await uploadFileToBackend(fileData);
        devLog('Upload completed for:', fileData.name, 'New ID:', uploadedFile.id);
        
        // Update with backend ID and completed status
        setUploadingFiles(prev => 
          prev.map(f => 
            f.id === fileData.id 
              ? { ...uploadedFile, progress: 100, status: 'completed' }
              : f
          )
        );
        
        return uploadedFile;
      } catch (error) {
        // Update status to error
        setUploadingFiles(prev => 
          prev.map(f => 
            f.id === fileData.id 
              ? { ...f, status: 'error', errorMessage: error instanceof Error ? error.message : 'Upload failed' }
              : f
          )
        );
        throw error;
      }
    });
    
    // Use Promise.allSettled instead of Promise.all to handle partial failures
    // This allows successful uploads to complete even if some fail
    const results = await Promise.allSettled(uploadPromises);

    // Extract successfully uploaded files
    const completedFiles = results
      .filter((result): result is PromiseFulfilledResult<UploadedFile> =>
        result.status === 'fulfilled'
      )
      .map(result => result.value);

    // Log any failures
    const failedUploads = results.filter(result => result.status === 'rejected');
    if (failedUploads.length > 0) {
      console.error(`${failedUploads.length} upload(s) failed:`,
        failedUploads.map((r, i) => ({
          index: i,
          error: r.status === 'rejected' ? r.reason?.message || r.reason : 'Unknown error'
        }))
      );
    }

    // Notify parent component about successfully uploaded files
    if (completedFiles.length > 0) {
      devLog('Calling onFilesUploaded');
      devLog('Completed files:', completedFiles.length);
      devLog('Failed files:', failedUploads.length);
      devLog('onFilesUploaded exists?', !!onFilesUploaded);

      if (onFilesUploaded) {
        onFilesUploaded(completedFiles);
        devLog('onFilesUploaded called successfully');
      } else {
        console.error('No onFilesUploaded callback provided');
      }
    } else {
      console.error('All uploads failed - no files to notify');
    }
  }, [maxFiles, maxFileSize, acceptedTypes, onFilesUploaded, customerId]);

  // Batch rename table confirm/cancel — one click uploads every row at once.
  const handleBatchRenameConfirm = useCallback((rows: Array<{ file: File; newName: string; documentType?: string }>) => {
    setRenameDialog({ isOpen: false, files: [] });
    processRenamedFiles(rows.map((r) => ({ file: r.file, newName: r.newName, documentType: r.documentType || 'Other' })));
  }, [processRenamedFiles]);

  const handleBatchRenameCancel = useCallback(() => {
    setRenameDialog({ isOpen: false, files: [] });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (!disabled && e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles, disabled]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  }, [handleFiles]);

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const removeFile = (fileId: string) => {
    setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status: UploadedFile['status']) => {
    switch (status) {
      case 'uploading': return 'text-blue-600';
      case 'processing': return 'text-amber-600';
      case 'completed': return 'text-green-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusText = (status: UploadedFile['status']) => {
    switch (status) {
      case 'uploading': return 'Uploading...';
      case 'processing': return 'Processing...';
      case 'completed': return 'Complete';
      case 'error': return 'Error';
      default: return 'Unknown';
    }
  };

  const getFileTypeIcon = (file: UploadedFile) => {
    const isEmail = file.type === 'message/rfc822' || 
                   file.type === 'application/vnd.ms-outlook' ||
                   file.name.toLowerCase().endsWith('.eml') ||
                   file.name.toLowerCase().endsWith('.msg');
    
    if (isEmail) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
        </svg>
      );
    }
    
    // Default document icon
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
      </svg>
    );
  };

  const getFileTypeLabel = (file: UploadedFile) => {
    const isEmail = file.type === 'message/rfc822' || 
                   file.type === 'application/vnd.ms-outlook' ||
                   file.name.toLowerCase().endsWith('.eml') ||
                   file.name.toLowerCase().endsWith('.msg');
    
    if (isEmail) {
      return 'Email';
    }
    
    if (file.type === 'application/pdf') return 'PDF';
    if (file.type.includes('word')) return 'Word';
    if (file.type.includes('spreadsheet') || file.type.includes('excel')) return 'Excel';
    
    return 'Document';
  };

  return (
    <div className={className}>
      {inlineError && (
        <div className="mb-3 px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
          {inlineError}
        </div>
      )}
      {/* Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
          disabled 
            ? 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-50'
            : isDragOver
              ? 'border-[#1993e5] bg-blue-50 cursor-pointer'
              : 'border-[#d0dee7] hover:border-[#1993e5] hover:bg-slate-50 cursor-pointer'
        }`}
        onDragOver={disabled ? undefined : handleDragOver}
        onDragLeave={disabled ? undefined : handleDragLeave}
        onDrop={disabled ? undefined : handleDrop}
        onClick={disabled ? undefined : handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',') + ',.pdf,.doc,.docx,.xls,.xlsx,.csv,.eml,.msg'}
          onChange={handleFileInputChange}
          className="hidden"
        />
        
        <div className="space-y-4">
          <div className="mx-auto w-12 h-12 text-[#4e7a97]">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>
          </div>
          
          <div>
            <p className="text-lg font-medium text-[#0e161b] mb-2">
              Drop your policy documents here
            </p>
            <p className="text-sm text-[#4e7a97] mb-4">
              or <span className="text-[#1993e5] font-medium">browse files</span> to upload
            </p>
            <p className="text-xs text-[#4e7a97]">
              Supports PDF, DOC, DOCX, Excel, CSV, and email files (.eml, .msg) up to {maxFileSize}MB each
            </p>
          </div>
        </div>
      </div>

      {/* File List */}
      {uploadingFiles.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="text-sm font-medium text-[#0e161b]">Upload Progress</h4>
          {uploadingFiles.map((file) => (
            <div key={file.id} className="border border-[#d0dee7] rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="text-[#4e7a97] flex-shrink-0">
                    {getFileTypeIcon(file)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-[#0e161b] truncate">
                        {file.name}
                      </p>
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full flex-shrink-0">
                        {getFileTypeLabel(file)}
                      </span>
                    </div>
                    <p className="text-xs text-[#4e7a97]">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 ml-4">
                  <span className={`text-xs font-medium ${getStatusColor(file.status)}`}>
                    {getStatusText(file.status)}
                  </span>
                  
                  <button
                    onClick={() => removeFile(file.id)}
                    className="text-[#4e7a97] hover:text-red-600 transition-colors"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {file.status === 'error' && file.errorMessage && (
                <p className="text-xs text-red-600 mb-2">{file.errorMessage}</p>
              )}
              
              {(file.status === 'uploading' || file.status === 'processing' || file.status === 'ocr_processing') && (
                <div className="space-y-2">
                  <div className="w-full bg-[#d0dee7] rounded-full h-2">
                    <div
                      className="bg-[#1993e5] h-2 rounded-full transition-all duration-300"
                      style={{ width: `${file.progress}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#4e7a97]">
                    {file.status === 'uploading' && (
                      <>
                        <div className="animate-spin w-3 h-3 border border-[#4e7a97] border-t-transparent rounded-full" />
                        <span>Uploading...</span>
                      </>
                    )}
                    {file.status === 'processing' && (
                      <>
                        <div className="animate-spin w-3 h-3 border border-[#4e7a97] border-t-transparent rounded-full" />
                        <span>Processing document...</span>
                      </>
                    )}
                    {file.status === 'ocr_processing' && (
                      <>
                        <div className="animate-spin w-3 h-3 border border-orange-500 border-t-transparent rounded-full" />
                        <span className="text-orange-600">OCR processing (scanned document detected)...</span>
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {file.status === 'completed' && !hideIndividualSuccessMessages && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-600">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
                    </svg>
                    <span className="text-xs">Ready for analysis</span>
                  </div>
                  
                  {file.processingMetadata && (
                    <div className="text-xs text-gray-600 space-y-1">
                      {file.processingMetadata.extractionMethod && (
                        <div className="flex items-center gap-2">
                          {file.processingMetadata.extractionMethod === 'ocr_only' && (
                            <>
                              <span className="inline-block w-2 h-2 bg-orange-400 rounded-full"></span>
                              <span>Processed with OCR (scanned document)</span>
                            </>
                          )}
                          {file.processingMetadata.extractionMethod === 'hybrid' && (
                            <>
                              <span className="inline-block w-2 h-2 bg-blue-400 rounded-full"></span>
                              <span>Processed with enhanced PDF + OCR</span>
                            </>
                          )}
                          {file.processingMetadata.extractionMethod === 'pdf_only' && (
                            <>
                              <span className="inline-block w-2 h-2 bg-green-400 rounded-full"></span>
                              <span>Processed with standard PDF extraction</span>
                            </>
                          )}
                        </div>
                      )}
                      
                      {file.processingMetadata.extractionQuality && file.processingMetadata.confidence && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Quality:</span>
                          <span className={`capitalize ${
                            file.processingMetadata.extractionQuality === 'high' ? 'text-green-600' :
                            file.processingMetadata.extractionQuality === 'medium' ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {file.processingMetadata.extractionQuality}
                          </span>
                          <span className="text-gray-500">
                            ({Math.round(file.processingMetadata.confidence * 100)}% confidence)
                          </span>
                        </div>
                      )}
                      
                      {file.processingMetadata.ocrAttempted && (
                        <div className="flex items-center gap-2">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-orange-500">
                            <path d="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z" />
                          </svg>
                          <span className="text-orange-600">OCR was used for text extraction</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Batch rename table — one screen, one row per file */}
      <BatchRenameModal
        isOpen={renameDialog.isOpen}
        files={renameDialog.files}
        projectTitle="this project"
        onCancel={handleBatchRenameCancel}
        onConfirm={handleBatchRenameConfirm}
        showDocumentType
      />
    </div>
  );
};

export default FileUploader;